import React, { useMemo } from "react";

import { useTranslation } from "@lib/i18n";
import { useConfigurationSelector } from "@redux/reducers/appConfig";
import { useIsKeyAccount } from "@redux/reducers/auth";

import CarteGriseDetail from "./CarteGriseDetail";
import CitroenRegistrationPrice from "./CitroenRegistrationPrice";
import Typography from "@components/shared/Typography";
import InfoIcon from "@components/shared/Icons/InfoIcon";

import { dealType } from "@types";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { getIsLeasingJourneySelected } from "@shared/v2/lib/getIsLeasingJourneySelected";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";
import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";
import { getDealMyFilesConfig } from "@lib/getDealMyFilesConfig";

const RegistrationSummary = ({ deal }) => {
  const { t } = useTranslation();
  const appConfig = useConfigurationSelector();
  const isKeyAccount = useIsKeyAccount();

  const { financeSimulation } = deal;
  const {
    vehicleRegistration,
    vehicleRegistrationRegion,
    journey,
  } = financeSimulation;

  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    vehicleRegistration
  );
  const isCashJourneySelected = getIsCashJourneySelected(journey);
  const isLeasingJourneySelected = getIsLeasingJourneySelected(journey);
  const isCarteGriseVisible = !!getCarteGriseAmount(
    financeSimulation,
    true,
    false
  );

  const dealMyFilesConfig = getDealMyFilesConfig(deal, appConfig);
  const isMyFilesRegistrationRegionSelectEnabled =
    !isKeyAccount &&
    !!(dealMyFilesConfig.enabled && dealMyFilesConfig.registrationRegionSelect);

  const summaryText = useMemo(() => {
    if (isCarteGriseVisible && isRegistrationByCitroen) {
      return t(
        `basket.vehicleRegistration.carteGrise.${
          isCashJourneySelected ? "cash" : "finance"
        }`
      );
    }
    if (isCashJourneySelected) {
      return t(
        `basket.vehicleRegistration.${
          isRegistrationByCitroen ? "optionCitroen" : "optionByMyself"
        }.button`
      );
    }
    if (isLeasingJourneySelected) {
      return t(`basket.vehicleRegistration.financeDescriptionPart1`);
    }
  }, [
    isRegistrationByCitroen,
    isCarteGriseVisible,
    isCashJourneySelected,
    isLeasingJourneySelected,
  ]);

  return (
    <>
      <Typography className="font-semibold my-4">{summaryText}</Typography>
      {isRegistrationByCitroen && (
        <CitroenRegistrationPrice deal={deal} withLabel />
      )}
      {isRegistrationByCitroen && !!isCarteGriseVisible && (
        <CarteGriseDetail deal={deal} readOnly={true} />
      )}
      {isMyFilesRegistrationRegionSelectEnabled && vehicleRegistrationRegion && (
        <>
          <div className="flex items-center justify-between w-full gap-4 border-t border-t-greyLight mt-8 pt-4">
            <div className="text-black">
              {t("basket.vehicleRegistration.registrationRegion.labelStatic")}
            </div>
            <div className="text-primaryDark font-semibold">
              {vehicleRegistrationRegion.countyCode} -{" "}
              {vehicleRegistrationRegion.label}
            </div>
          </div>
          <div className="flex items-start gap-2 pl-2 pt-4">
            <InfoIcon className="shrink-0 mt-1 text-primaryDark" />
            <Typography className="text-sm text-primaryDark">
              {t("basket.vehicleRegistration.registrationRegion.explanation")}
            </Typography>
          </div>
        </>
      )}
    </>
  );
};

RegistrationSummary.propTypes = {
  deal: dealType.isRequired,
};

export default RegistrationSummary;
